export interface ICalculatedValueGetter<TValue, TCalculate = TValue>
{
  calculateValue (): TCalculate,
  get value (): TValue
}

function createComputedValueGetterClass ()
{
  abstract class ComputedValueGetterBase<T> implements ICalculatedValueGetter<T>
  {
    #dataStore: { value?: T } = {};
    isLazy: boolean;

    constructor (opts: { isLazy?: boolean, calculateInitialValueIfNotLazy: boolean })
    {
      this.isLazy = opts.isLazy === true;
      if (this.isLazy === false && opts.calculateInitialValueIfNotLazy === true)
      {
        this.calculateValue();
      }
    }

    /** `calculateValue()` will alway calculate a new value, even if it has already been calculated.
     * 
     * Use the `value` getter to get the cached calculation.
     */
    calculateValue (): T
    {
      const value = this.__calculateValue();
      this.#dataStore.value = value;
      return value;
    }

    protected get needsCalculation ()
    {
      return ("value" in this.#dataStore) === false;
    }

    protected abstract __calculateValue (): T;
    get value ()
    {
      if (this.needsCalculation)
      {
        return this.calculateValue();
      }
      return this.#dataStore.value as T;
    }
  }
  return class ComputedValueGetter<T> extends ComputedValueGetterBase<T> implements ICalculatedValueGetter<T>
  {
    #computeFn: () => T = (undefined as unknown) as (() => T);
    constructor (opts: { isLazy?: boolean, computeFn: () => T })
    {
      super({ ...opts, calculateInitialValueIfNotLazy: false });
      this.#computeFn = opts.computeFn;
      if (this.isLazy === false)
      {
        this.calculateValue();
      }
    }

    override __calculateValue ()
    {
      return this.#computeFn();
    }
  };
}

const mcwd: Record<string, unknown> = (globalThis[ "@mcwd" ] as Record<string, unknown> ?? {});
globalThis[ "@mcwd" ] ??= mcwd;

export type ComputedValueGetter = ReturnType<typeof createComputedValueGetterClass>;
export const ComputedValueGetter = (mcwd.ComputedValueGetter ?? createComputedValueGetterClass()) as ComputedValueGetter;

mcwd.ComputedValueGetter ??= ComputedValueGetter;